import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { Observable, of, combineLatest, fromEvent, Subject } from 'rxjs';
import {
  ITransaction,
  ScrollDirection,
  TransactionChannel,
  TransactionFiltersInitial,
  TransactionFiltersParams,
  TransactionStatus,
} from '../models';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { MatSidenav } from '@angular/material/sidenav';
import { fadeAnimation } from '../animations';
import { map, take } from 'rxjs/operators';
import * as GLOBALS from '../transactions-global.const.values';

import { ReportsDialogComponentModel } from '../components/csv-reports-dialog/reports-dialog-component-model';
import { MatDialog } from '@angular/material/dialog';
import { CsvReportsDialogComponent } from '../components/csv-reports-dialog/csv-reports-dialog.component';
import { TransactionsFacade } from '../../+state/transactions/transactions.facade';
import { MatchMediaService } from 'libs/odin-core/src/lib/services/match-media.service';
import { ErrorReferenceService } from '@odin/odin-core';

const loaded$ = new Subject<void>();

const SEARCH_PLACEHOLDER_DESKTOP = 'Search by name, amount or last 4 digits';
const SEARCH_PLACEHOLDER_MOBILE = 'Search by name, amount, digits...';

@Component({
  standalone: false,
  selector: 'odin-transactions-list-page',
  templateUrl: './transactions-list-page.component.html',
  styleUrls: ['./transactions-list-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [fadeAnimation],
  // providers: [
  //   {
  //     provide: LOADED,
  //     useValue: loaded$.asObservable(),
  //   },
  // ],
})
export class TransactionsListPageComponent implements OnInit, AfterViewInit {
  transactions$!: Observable<ITransaction[]>;
  error$!: Observable<string>;
  allowSelection$!: Observable<boolean>;
  transactionFilters$!: Observable<TransactionFiltersInitial>;
  transactionChannels$!: Observable<TransactionChannel[]>;
  transactionStatuses$!: Observable<TransactionStatus[]>;
  params$!: Observable<TransactionFiltersParams>;
  loading$!: Observable<boolean>;
  hasNext$!: Observable<boolean>;
  hasPrev$!: Observable<boolean>;
  noMoreToLoad$!: Observable<boolean>;
  scroll$!: Observable<boolean>;
  public refreshEvent: EventEmitter<void> = new EventEmitter<void>();
  private readonly errorReference = inject(ErrorReferenceService);
  private errorTextCache?: { error: unknown; text: string };

  // Cached so a string error keeps one reference across change detection runs.
  errorText(message: string, error: unknown): string {
    if (this.errorTextCache?.error !== error) {
      this.errorTextCache = { error, text: this.errorReference.formatMessage(message, error) };
    }
    return this.errorTextCache?.text ?? message;
  }

  searchInputValue$: Observable<string> = of('');
  isDesktopLayout = true;
  tabletQuery!: MediaQueryList;
  mobileQuery!: MediaQueryList;
  isTouch = false;
  filterIcon: IconDefinition = faFilter;
  searchControlPlaceholder = SEARCH_PLACEHOLDER_DESKTOP;
  colour = 'primary';

  @ViewChild('drawer') filters_sidenav!: MatSidenav;

  @HostListener('window:resize')
  detectScreenSize() {
    this.searchControlPlaceholder = this.mobileQuery.matches
      ? SEARCH_PLACEHOLDER_MOBILE
      : SEARCH_PLACEHOLDER_DESKTOP;
  }

  constructor(
    @Inject('fuzzySearch') private fuzzySearch: boolean,
    private facade: TransactionsFacade,
    public dialog: MatDialog,
    private mediaService: MatchMediaService,
  ) {
    this.isTouch = this.mediaService.isTouch();
    this.tabletQuery = this.mediaService.matchMedia(GLOBALS.MAX_SCREEN_WIDTH);
    this.mobileQuery = this.mediaService.matchMedia(GLOBALS.MIN_SCREEN_WIDTH);

    this.refreshEvent.subscribe(() => {
      this.refreshTransactions();
    })
  }

  ngAfterViewInit(): void {
    const content = document.querySelector(GLOBALS.SCROLLED_CONTAINER);

    if (content) {
      this.scroll$ = fromEvent(content, 'scroll').pipe(
        map(() => content.scrollTop > GLOBALS.SCROLLED_TOP),
      );
    }
  }

  public fuzzySearchEnabled(): boolean {
    return this.fuzzySearch;
  }

  ngOnInit(): void {
    this.transactions$ = this.facade.loadTransactions$;

    // Only fetch when the store has nothing cached, so coming back from a
    // detail page reuses the already-loaded list. An explicit refresh (or a
    // filter/search/merchant change) still forces a fresh load.
    this.transactions$
      .pipe(take(1))
      .subscribe((transactions: ITransaction[]) => {
        if (!transactions?.length) {
          this.facade.getTransactions();
        }
      });

    this.loading$ = this.facade.getTransactionsLoader$;

    // get allow selection
    this.allowSelection$ = this.facade.getAllowSelection$;

    // get all params
    this.params$ = this.facade.getParams$;

    // get transactions filters from params
    this.getFiltersFromParams();

    // get search input from params
    this.getSearchValueFromParams();

    // catch error if any
    this.error$ = this.facade.getError$;

    // get pagination values
    this.hasNext$ = this.facade.getHasNext$;
    this.hasPrev$ = this.facade.getHasPrev$;
    this.noMoreToLoad$ = this.facade.noMoreToLoad$;

    // set placeholder for the search-bar
    this.searchControlPlaceholder = this.mobileQuery.matches
      ? SEARCH_PLACEHOLDER_MOBILE
      : SEARCH_PLACEHOLDER_DESKTOP;
  }

  // refresh transactions
  refreshTransactions() {
    this.facade.getTransactions();

    // complete the swipe down loader
    loaded$.next();
  }

  filterTransactions(filters: TransactionFiltersInitial) {
    // if new empty obj is dispatched search gets empty to leave it ????
    const filtersToBeSaved =
      filters === undefined ? new TransactionFiltersInitial() : filters;

    if (this.tabletQuery.matches) {
      this.filters_sidenav.close();
    }

    this.facade.saveFilters(filtersToBeSaved);

    this.facade.getTransactions();
  }

  searchTransactions(searchValue: string) {
    this.facade.saveSearchValue(searchValue);

    this.facade.getTransactions();
  }

  getFiltersFromParams() {
    this.transactionFilters$ = this.params$.pipe(
      map((params) => params.filters),
    );
  }

  getSearchValueFromParams() {
    this.searchInputValue$ = this.params$.pipe(
      map((params) => params.query || ''),
    );
  }

  loadTransactionsOnScroll(direction: ScrollDirection) {
    if (direction === ScrollDirection.Down) {
      this.loadMoreTransactions();
    }
  }

  public loadMoreTransactions(): void {
    // The reducer appends each page onto state.transactions, so the existing
    // transactions$ selector already carries the extra rows. This used to also
    // reassign transactions$ to concat(transactions$, loadMoreTransactions$) —
    // which never reached the second observable (a store selector never
    // completes) and only forced the async pipe to tear down and resubscribe,
    // re-emitting the whole list and repainting it, onto an ever deeper concat
    // chain.
    combineLatest([this.loading$, this.noMoreToLoad$])
      .pipe(take(1))
      .subscribe(([loading, noMoreToLoad]: [boolean, boolean]) => {
        if (loading || noMoreToLoad) return;
        this.facade.loadMoreTransactions();
      });
  }

  csvReportsDownload(data: ReportsDialogComponentModel) {
    const dialogRef = this.dialog.open(CsvReportsDialogComponent, {
      width: '600px',
      position: { top: '120px' },
      data: data,
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      // Remove error from store if any
      this.facade.clearCsvReportError();

      if (this.tabletQuery.matches) {
        this.filters_sidenav.close();
      }

      // eslint-disable-next-line no-empty
      if (result === 'download') {
      }
    });
  }
}
